// Admin-only "G1 Health" — FLEET-WIDE view of the relay's outbound Kalshi API
// load, scored in TOKENS vs each account's per-bucket tier limit (Kalshi's
// actual rate-limit currency), with an org drill-down. Fills the gap the
// caller-scoped Phase-2 StatusBar leaves.
//
// Kalshi rate limits (docs/getting_started/rate_limits): two token buckets PER
// ACCOUNT — read & write — default 10 tokens/request. Per-second refill by tier:
//   Basic    read 200  write 100
//   Advanced read 300  write 300
// The limits are PER ACCOUNT, so the meaningful signal is each account's % of
// its OWN limit — a fleet token SUM is meaningless (no fleet-wide bucket). The
// trend tracks the peak account's % of limit (all orgs, or the selected one).
//
// Source: GET /api/admin/g1-health (admin-gated) — per-account load + 429 state
// + tier + org names + the write-429 tape, reusing the relay's
// getKalshiLoadStats()/getOrgCacheHealth()/getWrite429Log().
//
// LANE CAVEAT: reflects whichever relay this build talks to (g1.pm = staging
// load; gbuff = live trader load).

import { useEffect, useRef, useState } from 'react';
import { Thermometer, AlertTriangle } from 'lucide-react';
import { apiFetch } from '@/stores/authStore';

interface RateLimitState {
  rateLimitedUntil: number;
  rateLimitStrikes: number;
  msUntilClear: number;
  throttled: boolean;
  keyPoolMode: string;
  channelCount: number;
  quarantinedCount: number;
}
interface RequestStats {
  windowMs: number;
  total: number;
  perSecond: number;
  byBucket: { trading: number; critical: number; general: number };
}
interface AccountLoad {
  key: string; // "org:<id>" | "user:<id>"
  ageMs: number;
  tier?: string; // "basic" | "advanced"
  rateLimit: RateLimitState;
  requests: RequestStats;
}
interface OrgCacheHealth {
  polling: boolean;
  orgs: Array<{
    orgId: string;
    ageMs: number;
    ok: boolean;
    consecutiveFailures: number;
    orderCount: number;
    suspectedEmptyOverwrites: number;
  }>;
}
interface Write429Entry {
  ts: number;
  userId: string;
  username: string | null;
  scope: string | null;
  orgId: string | null;
  op: string;
  action: string | null;
  ticker: string | null;
  orderId: string | null;
  message: string;
}
interface G1HealthSnapshot {
  serverTs: number;
  kalshiLoad: AccountLoad[];
  orgCache: OrgCacheHealth;
  write429?: Write429Entry[];
  orgNames?: Record<string, string>;
}

const POLL_MS = 3000;
const HISTORY = 80; // sparkline samples (~4 min at 3s)
const TOKEN_COST = 10; // Kalshi default tokens/request (non-default endpoints exist)

// Per-second token limits by tier (read / write). The relay only auto-upgrades
// to Advanced, so Basic/Advanced are the live cases; unknown → assume Basic.
const TIER_LIMITS: Record<string, { read: number; write: number }> = {
  basic: { read: 200, write: 100 },
  advanced: { read: 300, write: 300 },
};
function tierLimit(tier?: string) {
  return TIER_LIMITS[tier ?? 'basic'] ?? TIER_LIMITS.basic;
}

interface Enriched extends AccountLoad {
  readTokS: number;
  writeTokS: number;
  readPct: number;
  writePct: number;
  lim: { read: number; write: number };
}
function enrich(a: AccountLoad): Enriched {
  const rq = a.requests;
  const winSec = (rq?.windowMs || 10000) / 1000;
  const reads = (rq?.byBucket?.critical ?? 0) + (rq?.byBucket?.general ?? 0);
  const writes = rq?.byBucket?.trading ?? 0;
  const readTokS = (reads / winSec) * TOKEN_COST;
  const writeTokS = (writes / winSec) * TOKEN_COST;
  const lim = tierLimit(a.tier);
  return {
    ...a,
    readTokS,
    writeTokS,
    readPct: Math.round((readTokS / lim.read) * 100),
    writePct: Math.round((writeTokS / lim.write) * 100),
    lim,
  };
}

function loadTone(pct: number, throttled = false): string {
  if (throttled || pct >= 100) return 'text-red-400';
  if (pct >= 75) return 'text-amber-400';
  return 'text-foreground';
}
function shortKey(key: string): { scope: string; id: string } {
  const [scope, id = ''] = key.split(':');
  return { scope, id: id.length > 10 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id };
}
function orgIdOf(key: string): string | null {
  return key.startsWith('org:') ? key.slice(4) : null;
}
function fmtAge(ms: number): string {
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m${String(s % 60).padStart(2, '0')}`;
}

// Area sparkline. preserveAspectRatio="none" stretches the viewBox to full width
// (fine for line/area); the readout lives in the HTML caption, NOT as SVG text
// (which would smear). `refValue` draws a dashed danger line (e.g. the 100%
// limit). non-scaling-stroke keeps lines crisp under the stretch.
function Sparkline({
  values,
  color,
  height = 48,
  refValue,
}: {
  values: number[];
  color: string;
  height?: number;
  refValue?: number;
}) {
  const W = 600;
  const H = height;
  const max = Math.max(1, refValue ?? 0, ...values);
  const n = values.length;
  const y = (v: number) => H - (v / max) * (H - 4) - 2;
  const xs = n <= 1 ? [0, W] : values.map((_, i) => (i / (n - 1)) * W);
  const ys = n <= 1 ? [y(values[0] ?? 0), y(values[0] ?? 0)] : values.map(y);
  const line = xs.map((xv, i) => `${i === 0 ? 'M' : 'L'}${xv.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
  const area = `${line} L${W},${H} L0,${H} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" height={H} role="img">
      <line
        x1={0}
        y1={H - 0.5}
        x2={W}
        y2={H - 0.5}
        stroke="currentColor"
        className="text-border"
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      {refValue != null && (
        <line
          x1={0}
          y1={y(refValue)}
          x2={W}
          y2={y(refValue)}
          stroke="#ef4444"
          strokeWidth={1}
          strokeDasharray="4 3"
          opacity={0.6}
          vectorEffect="non-scaling-stroke"
        />
      )}
      <path d={area} fill={color} fillOpacity={0.12} />
      <path d={line} fill="none" stroke={color} strokeWidth={1.75} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'danger' | 'warn' | 'normal' }) {
  const color = tone === 'danger' ? 'text-red-400' : tone === 'warn' ? 'text-amber-400' : 'text-foreground';
  return (
    <div className="border-border rounded-lg border px-4 py-3">
      <div className="text-muted-foreground text-[11px] uppercase tracking-wide">{label}</div>
      <div className={`mt-1 font-mono text-2xl font-semibold ${color}`}>{value}</div>
      {sub && <div className="text-muted-foreground mt-0.5 text-[11px]">{sub}</div>}
    </div>
  );
}

// Running tape of the most recent write 429s — order placements Kalshi
// rate-limited. The money-critical signal: a write 429 = an order that did NOT
// land (writes are never paused by our read backoff, so this is a real reject).
function WriteLog({ entries, serverTs }: { entries: Write429Entry[]; serverTs: number }) {
  return (
    <section className="border-border rounded-lg border p-4">
      <h2 className="text-foreground mb-1 text-sm font-semibold">
        Recent write 429s
        <span className="text-muted-foreground ml-2 text-[11px] font-normal">
          order placements Kalshi rate-limited — the money-critical bucket (last {entries.length})
        </span>
      </h2>
      {entries.length === 0 ? (
        <p className="text-muted-foreground py-3 text-center text-xs">
          None recorded — no order placement has been write-429&apos;d here. 👍
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-muted-foreground border-border border-b">
              <tr>
                <th className="py-1.5 pr-3 font-medium">When</th>
                <th className="px-3 font-medium">Account</th>
                <th className="px-3 font-medium">Order</th>
                <th className="px-3 font-medium">Detail</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {entries.map((e, i) => {
                const acct = e.username ? e.username : shortKey(`${e.scope ?? 'user'}:${e.userId}`).id;
                return (
                  <tr key={`${e.ts}-${i}`} className="border-border/50 border-b">
                    <td className="text-muted-foreground whitespace-nowrap py-1.5 pr-3">
                      {fmtAge(Math.max(0, serverTs - e.ts))} ago
                    </td>
                    <td className="px-3">
                      <span className="text-muted-foreground">{e.scope ?? 'user'}</span>
                      <span className="text-foreground ml-1">{acct}</span>
                    </td>
                    <td className="text-foreground whitespace-nowrap px-3">
                      {e.op}
                      {e.action ? ` ${e.action}` : ''}
                      {e.ticker ? <span className="text-muted-foreground ml-1">{e.ticker}</span> : null}
                    </td>
                    <td className="px-3 text-red-400" title={e.message}>
                      {e.message.length > 70 ? `${e.message.slice(0, 70)}…` : e.message}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

interface LoadSample {
  fleetPeak: number; // worst account's max(read%,write%) across the fleet
  byOrg: Record<string, number>; // per-org worst account %
}

export default function G1Health() {
  const [snap, setSnap] = useState<G1HealthSnapshot | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<string>('all');
  const loadHist = useRef<LoadSample[]>([]);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    const load = async () => {
      try {
        const res = await apiFetch('/api/admin/g1-health');
        if (!res.ok) {
          if (!cancelled.current) setErr(`HTTP ${res.status}`);
          return;
        }
        const data = (await res.json()) as G1HealthSnapshot;
        if (cancelled.current) return;
        // Accumulate the % of limit (peak account), fleet-wide and per-org, so
        // the trend stays meaningful under the org drill-down.
        const byOrg: Record<string, number> = {};
        let fleetPeak = 0;
        for (const a of data.kalshiLoad) {
          const e = enrich(a);
          const pct = Math.max(e.readPct, e.writePct);
          fleetPeak = Math.max(fleetPeak, pct);
          const oid = orgIdOf(a.key);
          if (oid) byOrg[oid] = Math.max(byOrg[oid] ?? 0, pct);
        }
        loadHist.current = [...loadHist.current, { fleetPeak, byOrg }].slice(-HISTORY);
        setSnap(data);
        setErr(null);
      } catch (e) {
        if (!cancelled.current) setErr(e instanceof Error ? e.message : 'request failed');
      }
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => {
      cancelled.current = true;
      clearInterval(id);
    };
  }, []);

  const orgNames = snap?.orgNames ?? {};
  const enriched = (snap?.kalshiLoad ?? []).map(enrich); // whole fleet
  // Org drill-down: 'all' or a specific orgId. Selecting an org also hides
  // personal (user:) accounts.
  const inSel = (orgId: string | null) => selectedOrg === 'all' || orgId === selectedOrg;
  const filtered = enriched.filter((e) => inSel(orgIdOf(e.key)));
  const sorted = [...filtered].sort((a, b) => Math.max(b.readPct, b.writePct) - Math.max(a.readPct, a.writePct));
  const peakRead = filtered.reduce((m, e) => Math.max(m, e.readPct), 0);
  const peakWrite = filtered.reduce((m, e) => Math.max(m, e.writePct), 0);
  const throttledCount = filtered.filter((e) => e.rateLimit?.throttled).length;
  const write429 = (snap?.write429 ?? []).filter((w) => inSel(w.orgId));
  const orgCacheOrgs = (snap?.orgCache.orgs ?? []).filter((o) => inSel(o.orgId));

  // Dropdown options = orgs actually present in the data, labeled by name.
  const presentOrgIds = new Set<string>();
  for (const e of enriched) {
    const id = orgIdOf(e.key);
    if (id) presentOrgIds.add(id);
  }
  for (const o of snap?.orgCache.orgs ?? []) presentOrgIds.add(o.orgId);
  for (const w of snap?.write429 ?? []) if (w.orgId) presentOrgIds.add(w.orgId);
  const orgOptions = [...presentOrgIds]
    .map((id) => ({ id, label: orgNames[id] ?? shortKey(`org:${id}`).id }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Trend series: peak % of limit, respecting the org selection.
  const series = loadHist.current.map((s) => (selectedOrg === 'all' ? s.fleetPeak : s.byOrg[selectedOrg] ?? 0));
  const curLoad = series.length ? series[series.length - 1] : 0;
  const peakLoad = Math.max(0, ...series);

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mx-auto max-w-5xl space-y-5">
        <header>
          <div className="flex items-center gap-2">
            <Thermometer size={20} className="text-orange-400" />
            <h1 className="text-foreground text-xl font-bold">G1 Health</h1>
            <span className="text-muted-foreground ml-2 text-xs">
              Per-account Kalshi load in <span className="text-foreground">tokens</span>, vs each account&apos;s
              tier limit
            </span>
            {snap && (
              <select
                value={selectedOrg}
                onChange={(ev) => setSelectedOrg(ev.target.value)}
                className="border-border bg-muted text-foreground ml-auto rounded-md border px-2 py-1 text-xs"
                title="Drill into one org, or show the whole fleet"
              >
                <option value="all">All orgs ({orgOptions.length})</option>
                {orgOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
          </div>
          <p className="text-muted-foreground mt-2 text-xs leading-snug">
            Kalshi meters two per-account token buckets — read &amp; write, ~{TOKEN_COST} tokens/request.
            Limits/sec: <span className="text-foreground">Basic 200r/100w</span>,{' '}
            <span className="text-foreground">Advanced 300r/300w</span>. % = token rate ÷ that account&apos;s
            limit. Read/write buckets are independent — a 429 backoff sheds reads only, never order placement
            (writes). Reflects this build&apos;s relay (g1.pm = staging; gbuff = live trader load).
          </p>
        </header>

        {err && (
          <div className="flex items-center gap-2 rounded-lg bg-red-500/10 px-4 py-2.5 text-sm text-red-400">
            <AlertTriangle size={15} /> Couldn&apos;t load metrics: {err}
          </div>
        )}
        {!snap && !err && <p className="text-muted-foreground py-12 text-center text-sm">Loading fleet…</p>}

        {snap && (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label={selectedOrg === 'all' ? 'Active accounts' : 'Accounts in org'} value={String(filtered.length)} />
              <Stat
                label="Peak read load"
                value={`${peakRead}%`}
                sub="of an account's read limit"
                tone={peakRead >= 100 ? 'danger' : peakRead >= 75 ? 'warn' : 'normal'}
              />
              <Stat
                label="Peak write load"
                value={`${peakWrite}%`}
                sub="of an account's write limit"
                tone={peakWrite >= 100 ? 'danger' : peakWrite >= 75 ? 'warn' : 'normal'}
              />
              <Stat
                label="In 429 backoff"
                value={String(throttledCount)}
                tone={throttledCount > 0 ? 'danger' : 'normal'}
              />
            </div>

            <WriteLog entries={write429} serverTs={snap.serverTs} />

            <section className="border-border rounded-lg border p-4">
              <div className="mb-2 flex items-baseline justify-between">
                <h2 className="text-foreground text-sm font-semibold">
                  Peak load — % of limit
                  <span className="text-muted-foreground ml-2 text-[11px] font-normal">
                    {selectedOrg === 'all' ? 'worst account across the fleet' : 'worst account in this org'} ·
                    100% = at the bucket limit
                  </span>
                </h2>
                <span className="text-muted-foreground font-mono text-xs">
                  now {curLoad}% · peak {peakLoad}% · last ~{Math.round((HISTORY * POLL_MS) / 60000)}m
                </span>
              </div>
              <Sparkline
                values={series}
                color={peakLoad >= 100 ? '#ef4444' : peakLoad >= 75 ? '#f59e0b' : '#3b82f6'}
                refValue={100}
              />
            </section>

            <section className="border-border rounded-lg border p-4">
              <div className="mb-3 flex items-baseline justify-between">
                <h2 className="text-foreground text-sm font-semibold">Per-account token load</h2>
                <span className="text-muted-foreground text-[11px]">
                  tokens/sec over the relay&apos;s 10s window · sorted by tightest bucket
                </span>
              </div>
              {sorted.length === 0 ? (
                <p className="text-muted-foreground py-6 text-center text-xs">
                  {selectedOrg === 'all'
                    ? 'No active account clients right now (the relay caches one per org pool / personal account; an idle relay shows none).'
                    : 'No active account for this org right now.'}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="text-muted-foreground border-border border-b">
                      <tr>
                        <th className="py-1.5 pr-3 font-medium">Account</th>
                        <th className="px-3 font-medium">Tier</th>
                        <th className="px-3 text-right font-medium" title="read tokens/sec · % of read limit">Read</th>
                        <th className="px-3 text-right font-medium" title="write tokens/sec · % of write limit">Write</th>
                        <th className="px-3 text-center font-medium" title="read backoff active — writes still flow">429</th>
                        <th className="px-3 text-right font-medium" title="backoff escalation depth">Strikes</th>
                        <th className="px-3 text-right font-medium" title="ms until backoff clears">Clears in</th>
                        <th className="px-3 text-right font-medium" title="live / total keys">Keys</th>
                        <th className="px-3 text-right font-medium">Age</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono">
                      {sorted.map((e) => {
                        const k = shortKey(e.key);
                        const oid = orgIdOf(e.key);
                        const name = oid ? orgNames[oid] ?? k.id : k.id;
                        const rl = e.rateLimit;
                        return (
                          <tr key={e.key} className={`border-border/50 border-b ${rl.throttled ? 'bg-red-500/10' : ''}`}>
                            <td className="py-1.5 pr-3">
                              <span className="text-muted-foreground">{k.scope}</span>
                              <span className="text-foreground ml-1">{name}</span>
                            </td>
                            <td className="px-3">
                              <span className={e.tier === 'advanced' ? 'text-green-400' : 'text-amber-400'}>
                                {e.tier ?? 'basic'}
                              </span>
                            </td>
                            <td
                              className={`px-3 text-right ${loadTone(e.readPct, rl.throttled)}`}
                              title={`${e.readTokS.toFixed(0)} of ${e.lim.read} read tok/s`}
                            >
                              {e.readTokS.toFixed(0)}
                              <span className="text-muted-foreground">t/s</span>{' '}
                              <span className="font-semibold">{e.readPct}%</span>
                            </td>
                            <td
                              className={`px-3 text-right ${loadTone(e.writePct)}`}
                              title={`${e.writeTokS.toFixed(0)} of ${e.lim.write} write tok/s — writes are never paused by the read backoff`}
                            >
                              {e.writeTokS.toFixed(0)}
                              <span className="text-muted-foreground">t/s</span>{' '}
                              <span className="font-semibold">{e.writePct}%</span>
                            </td>
                            <td className="px-3 text-center">
                              {rl.throttled ? (
                                <span className="rounded bg-red-600/80 px-1.5 py-0.5 text-[10px] font-bold text-white">
                                  THROTTLED
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className={`px-3 text-right ${rl.rateLimitStrikes > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                              {rl.rateLimitStrikes}
                            </td>
                            <td className="text-muted-foreground px-3 text-right">
                              {rl.msUntilClear > 0 ? `${(rl.msUntilClear / 1000).toFixed(1)}s` : '—'}
                            </td>
                            <td className={`px-3 text-right ${rl.quarantinedCount > 0 ? 'text-amber-400' : 'text-muted-foreground'}`}>
                              {rl.channelCount - rl.quarantinedCount}/{rl.channelCount}
                            </td>
                            <td className="text-muted-foreground px-3 text-right">{fmtAge(e.ageMs)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="border-border rounded-lg border p-4">
              <div className="mb-2 flex items-center gap-2">
                <h2 className="text-foreground text-sm font-semibold">Org broker cache</h2>
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                    snap.orgCache.polling ? 'bg-green-600/80 text-white' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {snap.orgCache.polling ? 'POLLING' : 'IDLE'}
                </span>
                <span className="text-muted-foreground text-[11px]">
                  one shared read poller per org — what replaced the per-trader fan-out
                </span>
              </div>
              {orgCacheOrgs.length === 0 ? (
                <p className="text-muted-foreground py-3 text-center text-xs">No orgs being polled.</p>
              ) : (
                <table className="w-full text-left text-xs font-mono">
                  <thead className="text-muted-foreground border-border border-b">
                    <tr>
                      <th className="py-1.5 pr-3 font-medium">Org</th>
                      <th className="px-3 text-right font-medium">Orders cached</th>
                      <th className="px-3 text-center font-medium">Healthy</th>
                      <th className="px-3 text-right font-medium" title="consecutive failed polls">Fails</th>
                      <th className="px-3 text-right font-medium" title="empty-overwrite guards tripped">Empty-guards</th>
                      <th className="px-3 text-right font-medium">Age</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgCacheOrgs.map((o) => (
                      <tr key={o.orgId} className={`border-border/50 border-b ${!o.ok ? 'bg-red-500/10' : ''}`}>
                        <td className="text-foreground py-1.5 pr-3">{orgNames[o.orgId] ?? shortKey(`org:${o.orgId}`).id}</td>
                        <td className="text-foreground px-3 text-right">{o.orderCount}</td>
                        <td className="px-3 text-center">
                          {o.ok ? <span className="text-green-400">ok</span> : <span className="font-bold text-red-400">STALE</span>}
                        </td>
                        <td className={`px-3 text-right ${o.consecutiveFailures > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                          {o.consecutiveFailures}
                        </td>
                        <td className={`px-3 text-right ${o.suspectedEmptyOverwrites > 0 ? 'text-amber-400' : 'text-muted-foreground'}`}>
                          {o.suspectedEmptyOverwrites}
                        </td>
                        <td className="text-muted-foreground px-3 text-right">{fmtAge(o.ageMs)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <p className="text-muted-foreground text-[11px]">
              server clock {new Date(snap.serverTs).toLocaleTimeString()} · polling every {POLL_MS / 1000}s ·
              token rate ≈ requests × {TOKEN_COST} (Kalshi default; some endpoints differ)
            </p>
          </>
        )}
      </div>
    </div>
  );
}
